import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClickAway } from 'react-use'
import { isFilterActive } from '@/services/utils'
import clsx from 'clsx'

import { ProjectFavorite, ProjectStatus } from '@makyoapp/makyo-sdk/models/Projects'
import { TeamAdapter } from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import {
  AvatarCell,
  AvatarSize,
  Button,
  ButtonVariant,
  Field,
  JustifyVariant,
  Label,
  Modal,
  projectStatusColor,
  SelectDropdown,
  Status,
} from '@makyoapp/makyo-ui'

import { useGetOrgTeams } from '@/resolvers/org/query'
import { initialAppFilterValueState, useOrgAppsContext } from '@/context/OrgAppsContext'

/**
 * App Filter Modal
 *
 * depends on `AppFilterContext`
 */
function AppFilterModal() {
  const { t } = useTranslation()
  const { filter, setFilter } = useOrgAppsContext()
  const ref = useRef(null)
  const [show, setShow] = useState<boolean>(false)

  useClickAway(ref, () => {
    setShow(false)
  })

  const { data: orgTeams } = useGetOrgTeams()
  const teams = orgTeams || []

  const isActive = isFilterActive(filter)

  return (
    <div className='relative inline-block'>
      <Button
        id='app-filter-btn'
        icon={<Icon.Filter16 />}
        variant={!show ? (isActive ? ButtonVariant.Normal : ButtonVariant.Ghost) : ButtonVariant.Normal}
        noPadding
        squared
        className='shrink-0'
        onClick={() => setShow(!show)}
      />
      {show && (
        <div
          ref={ref}
          className={clsx(
            'absolute z-10 top-full mt-3 shadow-xl dark:shadow-xl-alt border bdr',
            'ltr:right-0 ltr:left-auto rtl:right-auto rtl:left-0',
            'w-[280px] md:w-[320px] mk-bg'
          )}
        >
          <Modal.Header onCloseClick={() => setShow(false)}>{t('Filters')}</Modal.Header>
          <Modal.Body>
            <div>
              <Field>
                <Label>{t('Team')}</Label>
                <SelectDropdown
                  id='app-filter-team'
                  outlined={true}
                  value={filter.team}
                  onChange={(team: TeamAdapter) => {
                    setFilter({
                      ...filter,
                      team: team,
                    })
                  }}
                  optionLabel={(opt: TeamAdapter) => opt.name}
                  renderOption={(opt: TeamAdapter) => {
                    return opt.id === 'all' ? t(opt.id) : <AvatarCell size={AvatarSize.XS} {...opt} />
                  }}
                  options={[
                    {
                      id: 'all',
                      name: 'All',
                    },
                    ...teams,
                  ]}
                />
              </Field>
              <Field>
                <Label>{t('Status')}</Label>
                <SelectDropdown
                  id='app-filter-status'
                  outlined={true}
                  value={filter.status}
                  onChange={(v) => setFilter({ ...filter, status: v })}
                  options={['all', ProjectStatus.Draft, ProjectStatus.Published, ProjectStatus.Unpublished]}
                  renderOption={(opt) => {
                    return opt === 'all' ? (
                      t(opt)
                    ) : (
                      <div className='text-m-sm'>
                        <Status dotColor={projectStatusColor(opt)}>{opt}</Status>
                      </div>
                    )
                  }}
                />
              </Field>
              {/* Plan select */}
              {/* <Field>
                <Label>{t('Plan')}</Label>
                <SelectDropdown
                  id='app-filter-plan'
                  outlined={true}
                  value={filter.plan}
                  onChange={(v) => setFilter({ ...filter, plan: v })}
                  options={['all', 'FREE', 'PRO', 'PREMIUM', 'ELITE']}
                  renderOption={(opt) => {
                    return opt === 'all' ? (
                      t(opt)
                    ) : (
                      <div className='text-m-sm'>
                        <Badge color={projectPlanColor(opt)}>{opt}</Badge>
                      </div>
                    )
                  }}
                />
              </Field> */}
              {/* Favorite switch select */}
              <Field>
                <Label>{t('Favorite')}</Label>
                <SelectDropdown
                  id='app-filter-favorite'
                  outlined={true}
                  value={filter.favorite}
                  onChange={(v) => setFilter({ ...filter, favorite: v })}
                  options={['all', ProjectFavorite.True, ProjectFavorite.False]}
                  renderOption={(opt) => {
                    return t(opt)
                  }}
                />
              </Field>
            </div>
          </Modal.Body>
          <Modal.Footer justify={JustifyVariant.Start}>
            <Button
              variant={ButtonVariant.Ghost}
              onClick={() => {
                setFilter(initialAppFilterValueState)
              }}
            >
              {t('Reset')}
            </Button>
          </Modal.Footer>
        </div>
      )}
    </div>
  )
}

export default AppFilterModal
